import {useMutation, useQuery} from 'react-query';
import {BidDialogBody} from '../components/auction/biddialogbody';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  setBidAmount,
  setDownloadProgress,
  setWinnerPrice,
} from '../store/slices/auction';
import {setAppDialogParams} from '../store/slices/ui';
import {
  Auction,
  AuctionBid,
  AuctionBuyerKey,
  FolderType,
  Media,
  TransactionPayload,
  UserInfo,
} from '../types';
import {useCloseDialog} from './useCloseDialog';
import {
  downloadFileByMedia,
  exportFile,
  findBy,
  getById,
  sendChatMessage,
  shareFileToWT,
  writeItem,
} from '../api';
import {useFolder} from './useFolder';
import {useMemo} from 'react';
import {PayDialogBody} from '../components/auction/paydialogbody';
import {sendMatic, provider} from '../api/metamask';
import uuid from 'react-native-uuid';
import {
  decryptAesKey,
  decryptPayload,
  encryptAesKey,
  encryptPayload,
  generateAesKey,
  generateNewKeyPair,
} from '../api/ssl';
import {
  Block,
  ethers,
  TransactionReceipt,
  TransactionResponse,
} from 'ethers';
export function useAuctionActions(
  auctionId: string,
  auction: Auction | undefined,
  user: UserInfo,
) {
  // console.info('auction', auction)
  const dispatch = useAppDispatch();
  const {closeDialog} = useCloseDialog();
  const {folderContent} = useFolder(
    auctionId,
    undefined,
    undefined,
    FolderType.auction,
    undefined,
    auction?.mnemonic,
  );
  const currentBidItem = useMemo(() => {
    if (!folderContent || !folderContent?.length) return 0;
    const bids = folderContent.filter(i => i.isBid);
    const bid = bids?.length
      ? bids.sort((a, b) => b.amount - a.amount)?.[0]
      : undefined;
    return bid;
  }, [folderContent]);
  const auctionTransaction = useQuery(
    'auction_trx_' + auctionId,
    async () => {
      // const trxId = "0xc99fe6bd012ce0d5795c0e2d1d965f953808f1025a89ca792919589a65b5aade"
      const trxId = (auction as Auction)
        .winnerPaymentTransactionId as string;
      const trx:
        | (TransactionResponse & {
            block?: Block | null;
            receipt?: TransactionReceipt | null;
          })
        | null = await provider.getTransaction(trxId); //auction.winnerPaymentTransactionId as string)
      // // const block = await providerCloud.getBlock(trx?.blockHash as string)
      // trx.block = await trx?.getBlock()
      if (trx) {
        trx.block = await trx.getBlock();
        trx.receipt = await provider.getTransactionReceipt(trxId);
      }

      console.info('trx', trx, auction);
      return trx;
    },
    {
      refetchInterval: 20000,
      // d => {
      // if (!d?.data && d?.receipt?.status !== 1)
      // return 30000;
      // return false;
      // },
      enabled: auction?.winnerPaymentTransactionId !== undefined,
    },
  );
  const trxData = auctionTransaction.data;
  const currentBid = currentBidItem?.amount;
  const wallet = useAppSelector(state => state.auction.bidWallet);
  const amount = useAppSelector(state => state.auction.bidAmount);
  const createBidMutation = useMutation(async () => {
    if (isNaN(amount)) {
      console.error('invalid amount', amount);
      return;
    }
    if (amount < auction.minStep + auction.minPrice) {
      console.error(
        'bid too low',
        amount,
        auction.minStep + auction.minPrice,
      );
      return;
    }
    if (currentBid && amount < currentBid) {
      console.error('bid less than current bid', amount, currentBid);
      return;
    }
    let name = user.name;
    await sendChatMessage(
      auctionId,
      name + ' has created a bid of ' + amount,
      auction.mnemonic,
      {system: true, isBid: true, wallet, amount},
    );
  });
  const closeAuctionMutation = useMutation(async () => {
    let name = user.name;
    let message = currentBid
      ? name +
        ' has closed the auction with the winning bid of ' +
        currentBid
      : name + ' has closed this auction without a winner';
    await sendChatMessage(auctionId, message, auction.mnemonic, {
      system: true,
      isBid: true,
      wallet,
      amount,
    });
    auction.isClosed = true;
    auction.winnerId = (currentBidItem as AuctionBid).userId;
    auction.winnerPrice = currentBid;
    await writeItem(auction);
  });
  useMemo(() => {
    if (auction?.minStep) {
      const {minPrice, minStep} = auction;
      const nextBidValue =
        currentBid !== undefined
          ? parseFloat(minStep) + parseFloat(currentBid)
          : parseFloat(minPrice) + parseFloat(minStep);
      dispatch(
        setBidAmount(parseFloat(parseFloat(nextBidValue).toFixed(2))),
      );
    }
  }, [currentBid, auction]);
  const createBid = () => {
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Create Bid',
        appDialogIcon: 'currency-usd',
        appDialogOnSelectRight: async () => {
          await createBidMutation.mutateAsync();
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody: BidDialogBody,
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };

  const closeAuction = () => {
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Close Auction',
        appDialogIcon: 'currency-usd',
        appDialogOnSelectRight: async () => {
          await closeAuctionMutation.mutateAsync();
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'The auction will be closed and the winner will be assigned',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };
  const payWallet = useAppSelector(state => state.auction.payWallet);
  const pay = useMutation(async () => {
    try {
      const auctionKeyId = uuid.v4().toString();
      const keyPair =
        (await generateNewKeyPair()) as AuctionBuyerKey['keyPair'];
      const auctionKey: AuctionBuyerKey = {
        id: auctionKeyId,
        auctionId,
        keyPair,
        folderId: 'auction_keys',
        isKeyPair: true,
      };
      const {publicKey} = keyPair;
      await writeItem(auctionKey);
      console.info('auction.wallet', auction, auction.wallet);
      const transactionId = await sendMatic(
        payWallet,
        auction.wallet,
        auction.winnerPrice as number,
        {publicKey},
      );
      auction.winnerPaymentTransactionId = transactionId;
      await writeItem({
        ...auction,
        winnerPaymentTransactionId: transactionId,
      });
    } catch (err) {
      console.error('pay err', err);
    }
  });
  const payForAuction = () => {
    dispatch(setWinnerPrice(auction.winnerPrice as number));
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Pay For Auction',
        appDialogIcon: 'currency-usd',
        appDialogOnSelectRight: async () => {
          await pay.mutateAsync();
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody: PayDialogBody,
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };
  const releaseMutation = useMutation(async () => {
    const abiCoder = new ethers.AbiCoder();
    const pubKeyData = abiCoder.decode(
      ['string'],
      trxData?.data as string,
    );
    console.info('pubKeyData', pubKeyData);
    const payload = JSON.parse(pubKeyData[0]) as TransactionPayload;
    const pubKey = JSON.parse(
      payload.publicKey as string,
    ) as JsonWebKey;
    console.info('pubkey', pubKey);
    const mediaOriginal = (await getById(
      auction.mediaId,
      auction.media.folderId,
    )) as Media;
    console.info('mediaOriginal', mediaOriginal);
    if (!mediaOriginal) throw 'no_media_original_found';
    const aesKey = await generateAesKey();
    const encryptedKey = await encryptAesKey(aesKey.kp, pubKey);
    const {encryptedPayload, iv} = await encryptPayload(
      aesKey.key,
      JSON.stringify(mediaOriginal),
    );
    auction.encryptedPayload = encryptedPayload;
    auction.ivb = iv;
    auction.encryptedKey = encryptedKey;
    await writeItem(auction);
    await shareFileToWT(mediaOriginal.id, mediaOriginal.folderId);
    console.info('auction', auction);
  });
  const releaseDownload = () => {
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Release File',
        appDialogIcon: 'cloud-upload',
        appDialogOnSelectRight: async () => {
          await releaseMutation.mutateAsync();
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'The auction file will be released and the winner will be able to download',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
  };
  const downloadAuctionMedia = async () => {
    const [auctionKey] = (await findBy(
      'auctionId',
      auctionId,
    )) as AuctionBuyerKey[];
    console.info('downloadAuctionMedia', auction, auctionKey);
    const {encryptedKey, encryptedPayload, ivb} = auction;
    const aesKey = await decryptAesKey(
      encryptedKey as string,
      auctionKey.keyPair.privateKey as JsonWebKey,
    );
    console.info('decrypted aes key', aesKey);
    const payload = await decryptPayload(
      aesKey,
      encryptedPayload as string,
      ivb as string,
    );
    const media = JSON.parse(payload);
    console.info('decrypted payload', payload);
    media.folderId = 'new_media';
    await writeItem(media);
    let didDownload = false;
    await downloadFileByMedia(media, progress => {
      console.info('download progress', progress);
      dispatch(setDownloadProgress(progress));
      if (progress === 1) {
        console.info('finished');
        if (!didDownload) {
          didDownload = true;
          exportFile(media);
        }
      }
    });
  };
  return {
    createBid,
    closeAuction,
    currentBid,
    payForAuction,
    downloadAuctionMedia,
    releaseDownload,
    auctionTransaction: auctionTransaction.data,
  };
}
